/**
 * Closed permission catalog and the fixed role → permission mapping (REQUIREMENTS.md §1,
 * FR-13, SEC-4). Routes check permissions, never role names, so a fifth role (Contributor,
 * group access, 2026-09-28) was added without touching routes. Seeded into `permissions`, `roles`, `role_permissions`.
 */

export const ROLE_NAMES = [
  "PlatformAdmin",
  "TenantAdmin",
  "InstanceAdmin",
  "Contributor",
  "Viewer",
] as const;
export type RoleName = (typeof ROLE_NAMES)[number];

/**
 * Higher rank may grant lower ranks; never the reverse. Only the order means anything: nothing
 * stores a rank but the `roles` catalog, which the permission seed rewrites (Contributor, 0012,
 * moved the three above it up by one).
 */
export const ROLE_RANK: Record<RoleName, number> = {
  PlatformAdmin: 5,
  TenantAdmin: 4,
  InstanceAdmin: 3,
  Contributor: 2,
  Viewer: 1,
};

export const ROLE_LABELS: Record<RoleName, string> = {
  PlatformAdmin: "Platform admin",
  TenantAdmin: "Tenant admin",
  InstanceAdmin: "Instance admin",
  Contributor: "Contributor",
  Viewer: "Viewer",
};

export const SCOPE_TYPES = ["platform", "tenant", "instance"] as const;
export type ScopeType = (typeof SCOPE_TYPES)[number];

/** Permission keys: `<group>.<verb>`. Keep alphabetical within a group. */
export const PERMISSIONS = {
  // tenants, instances, bindings (FR-20, FR-21). `platform.customers.manage` folded into
  // `platform.tenants.manage` with the customers table (platform 0027, round 6 T-04).
  "platform.tenants.manage": { group: "platform", label: "Manage tenants" },
  "platform.instances.manage": { group: "platform", label: "Create and provision instances" },
  "platform.settings.manage": { group: "platform", label: "Manage platform settings" },
  "platform.connectors.manage": { group: "platform", label: "Manage connector definitions" },
  "platform.audit.read": { group: "platform", label: "Read the platform-wide audit trail" },
  // tenant scope
  "tenant.audit.read": { group: "tenant", label: "Read the tenant audit trail" },
  "tenant.audit.export": { group: "tenant", label: "Export the tenant audit trail" },
  "tenant.instances.manage": { group: "tenant", label: "Manage instances of the tenant" },
  "tenant.users.manage": { group: "tenant", label: "Manage users at tenant scope" },
  // instance scope
  // #1106 (owner, 2026-10-14): `instance.read` is the instance itself, held by every role for the
  // shell, Overview, Your data and the person's own Watches. The Settings content (retention,
  // alert routing, deliveries) is `instance.settings.read`, and the destination binding
  // `instance.binding.read`, both Instance admin and above.
  "instance.read": { group: "instance", label: "Read the instance" },
  "instance.binding.read": { group: "instance", label: "Read the destination binding" },
  // ADR 0011 (2026-10-08): the loader's user, role and service principal, withheld below
  // TenantAdmin from every response (`server/services/loaderIdentity.ts`).
  "instance.binding.identity.read": {
    group: "instance",
    label: "Read the destination's service account name and role",
  },
  "instance.binding.write": { group: "instance", label: "Change the destination binding" },
  "instance.users.read": { group: "instance", label: "List instance users" },
  "instance.users.manage": { group: "instance", label: "Grant and revoke instance access" },
  "instance.settings.read": {
    group: "instance",
    label: "Read instance settings and alert routing",
  },
  "instance.settings.write": { group: "instance", label: "Change instance settings" },
  "instance.audit.read": { group: "instance", label: "Read the instance audit trail" },
  "instance.audit.export": { group: "instance", label: "Export the audit trail" },
  // connectors and dataflows (FR-3x, FR-4x)
  "connectors.read": { group: "connections", label: "List connector definitions" },
  "dataflows.read": { group: "connections", label: "Read connections" },
  "dataflows.write": { group: "connections", label: "Create and edit connections" },
  "dataflows.delete": { group: "connections", label: "Delete connections" },
  "dataflows.validate": { group: "connections", label: "Validate credentials and browse sources" },
  // schedules (FR-5x)
  "schedules.read": { group: "schedules", label: "Read schedules" },
  "schedules.write": { group: "schedules", label: "Create and edit schedules" },
  "schedules.delete": { group: "schedules", label: "Delete schedules" },
  // runs
  "runs.read": { group: "runs", label: "Read runs and history" },
  "runs.start": { group: "runs", label: "Run on demand" },
  "runs.retry": { group: "runs", label: "Retry a failed step" },
  "runs.cancel": { group: "runs", label: "Cancel a run" },
  "runs.force_reset": { group: "runs", label: "Force-reset a stuck run" },
  // dbt jobs (FR-6x; round 6 T-29 renamed `transformations.*`, platform 0028)
  "dbt_jobs.read": { group: "dbt_jobs", label: "Read dbt jobs" },
  "dbt_jobs.write": { group: "dbt_jobs", label: "Configure dbt jobs" },
  // skyMonitor (platform migration `monitor`): data health reads and the check controls
  "monitor.read": { group: "monitor", label: "Read data health" },
  "monitor.manage": { group: "monitor", label: "Change data health monitors" },
} as const;

export type PermissionKey = keyof typeof PERMISSIONS;
export const PERMISSION_KEYS = Object.keys(PERMISSIONS) as PermissionKey[];

/**
 * #1106 (owner, 2026-10-14): no Settings content and no destination binding. Overview and Your data
 * say where the data lands; the destination's readiness (the banner) is on `instance.read`.
 */
const VIEWER: PermissionKey[] = [
  "instance.read",
  "connectors.read",
  "dataflows.read",
  "schedules.read",
  "runs.read",
  "dbt_jobs.read",
  "monitor.read",
];

/**
 * Group access (owner answer of 2026-09-28): edits connections and schedules and runs them. No
 * users, no instance settings, no destination, no audit trail, no force-reset of a stuck run and
 * no dbt job configuration: those stay with InstanceAdmin.
 *
 * #1105 (owner, 2026-10-14): no delete either. Removing a connection or a schedule, and with it
 * the schedules it was part of, is Instance admin and above (separation of duties).
 */
const CONTRIBUTOR: PermissionKey[] = [
  ...VIEWER,
  "dataflows.write",
  "dataflows.validate",
  "schedules.write",
  "runs.start",
  "runs.retry",
  "runs.cancel",
];

/**
 * #1067 (owner, option A, 2026-10-14): Admin > Users, the list of every person who reaches the
 * instance with their email and last sign-in, is for Instance admin and above. A Contributor or a
 * Viewer holds no `instance.users.read`.
 */
const INSTANCE_ADMIN: PermissionKey[] = [
  ...CONTRIBUTOR,
  // #1106: the Settings screens and the destination binding.
  "instance.binding.read",
  "instance.settings.read",
  // #1105: delete moved up from Contributor.
  "dataflows.delete",
  "schedules.delete",
  "instance.users.read",
  "instance.users.manage",
  "instance.settings.write",
  "instance.audit.read",
  "instance.audit.export",
  "runs.force_reset",
  "dbt_jobs.write",
  "monitor.manage",
];

const TENANT_ADMIN: PermissionKey[] = [
  ...INSTANCE_ADMIN,
  "instance.binding.identity.read",
  "tenant.audit.read",
  "tenant.audit.export",
  "tenant.instances.manage",
  "tenant.users.manage",
];

export const ROLE_PERMISSIONS: Record<RoleName, readonly PermissionKey[]> = {
  PlatformAdmin: PERMISSION_KEYS,
  TenantAdmin: TENANT_ADMIN,
  InstanceAdmin: INSTANCE_ADMIN,
  Contributor: CONTRIBUTOR,
  Viewer: VIEWER,
};

export function roleHasPermission(role: RoleName, permission: PermissionKey): boolean {
  return ROLE_PERMISSIONS[role].includes(permission);
}

/** Which roles a grantor holding `grantorRole` may grant (never above their own rank). */
export function grantableRoles(grantorRole: RoleName): RoleName[] {
  return ROLE_NAMES.filter((r) => ROLE_RANK[r] <= ROLE_RANK[grantorRole]);
}

/** The scope types a role can be assigned at. */
export const ROLE_SCOPES: Record<RoleName, readonly ScopeType[]> = {
  PlatformAdmin: ["platform"],
  TenantAdmin: ["tenant"],
  InstanceAdmin: ["instance"],
  Contributor: ["instance"],
  Viewer: ["instance"],
};

/**
 * The roles a directory group may carry as its default (group access, owner answer of
 * 2026-09-28): Viewer preselected, Contributor and InstanceAdmin allowed, TenantAdmin only from a
 * TenantAdmin (or above). Never PlatformAdmin: SEC-18 wants a reason and a person for that.
 */
export const GROUP_DEFAULT_ROLES = [
  "Viewer",
  "Contributor",
  "InstanceAdmin",
  "TenantAdmin",
] as const satisfies readonly RoleName[];
export type GroupDefaultRole = (typeof GROUP_DEFAULT_ROLES)[number];

/** The group default roles a grantor holding `grantorRole` may choose (the grant rank rule). */
export function groupDefaultRolesFor(grantorRole: RoleName): GroupDefaultRole[] {
  return GROUP_DEFAULT_ROLES.filter((role) => ROLE_RANK[role] <= ROLE_RANK[grantorRole]);
}

/** What an admin may raise one group-enrolled person to, while they stay in the group. */
export const OVERRIDE_ROLES = [
  "Contributor",
  "InstanceAdmin",
] as const satisfies readonly RoleName[];
export type OverrideRole = (typeof OVERRIDE_ROLES)[number];
